/**
 * The sample transition: TERN-4, a small drone from Ternline, the fictional
 * company in the vendor questionnaire's worked examples. Every name and figure
 * is fabricated, and every fact comes from those examples or the two documents
 * written to agree with them (documents.json). The readiness result is the
 * real engine's (internal/readiness Score) on those facts (readiness.json).
 *
 * It's served by services/transit.ts for any sample- ID (sample/api.ts), so the
 * workspace renders it with its own components; nothing here is in the database.
 */
import type {
  DraftBlock, GraphNode, InfoRequest, NodeStatus, Readiness, ReadinessResult,
  TransitDocument, Transition, TransitionGraph,
} from '../../../types/transit'
import texts from './documents.json'
import readinessResult from './readiness.json'

export const SAMPLE_TRANSITION_ID = 'sample-tern4'
const T = SAMPLE_TRANSITION_ID
const CREATED = '2026-10-20T14:00:00Z'

export const SAMPLE_TRANSITION: Transition = {
  id: T,
  org_id: 'sample',
  product_id: 'sample-product',
  name: 'TERN-4 (Example)',
  status: 'draft',
  created_by: null,
  created_at: CREATED,
  updated_at: '2026-10-22T16:00:00Z',
}

const QUESTIONNAIRE = 'Ternline_TERN-4_Vendor_Questionnaire.docx'
const CAPABILITY = 'Ternline_Capability_Statement.docx'
const LAB = 'TERN-4_Environmental_Test_Summary.docx'

// The section definitions, as transit.nodes holds them.
const DEFINITIONS: Array<{ key: string; name: string; description: string; position: number; order: number }> = [
  { key: 'transition_decision', name: 'Summary and Decision Requested', description: 'The decision requested, the package at a glance, and the recommended actions.', position: 10, order: 88 },
  { key: 'transition_case', name: 'Capability Description', description: 'What the capability does and how it performs, what has been fielded, and what transfers.', position: 20, order: 10 },
  { key: 'operational_need', name: 'Operational Need', description: 'The documented gaps the capability answers, and where the requirement stands.', position: 30, order: 20 },
  { key: 'program_offices', name: 'Candidate Receiving Program Offices', description: 'The program offices that could receive the capability, in order of mission fit.', position: 40, order: 30 },
  { key: 'technical_readiness', name: 'Technical Maturity and Test Evidence', description: 'TRL, MRL and IRL against the test and field evidence.', position: 50, order: 40 },
  { key: 'company_readiness', name: 'Company Viability', description: 'CRL: the three pillars, each on its own evidence, and the conditions to reach CRL 4.', position: 60, order: 50 },
  { key: 'funding_plan', name: 'Funding Plan', description: 'The bridge and the permanent line, and the tranches that release the bridge.', position: 70, order: 85 },
  { key: 'ownership_rights', name: 'Data Rights and Sustainment Data', description: 'Where the rights sit, what technical data exists, and the recommended rights position.', position: 80, order: 70 },
  { key: 'vendor_costs', name: 'Cost Roll-Up', description: 'Every vendor-quoted cost, in the form quoted.', position: 90, order: 80 },
  { key: 'sources_stakeholders', name: 'Appendix A. Sources and Stakeholders', description: 'Every source and the sections that cite it; the stakeholders and their roles.', position: 100, order: 90 },
]

const EDGES: Array<[string, string]> = [
  ['company_readiness', 'technical_readiness'],
  ['funding_plan', 'company_readiness'],
  ['funding_plan', 'ownership_rights'],
  ['funding_plan', 'vendor_costs'],
  ['ownership_rights', 'program_offices'],
  ['program_offices', 'operational_need'],
  ['program_offices', 'transition_case'],
  ['sources_stakeholders', 'transition_decision'],
  ['technical_readiness', 'program_offices'],
  ['transition_decision', 'company_readiness'],
  ['transition_decision', 'funding_plan'],
  ['transition_decision', 'operational_need'],
  ['transition_decision', 'ownership_rights'],
  ['transition_decision', 'technical_readiness'],
  ['transition_decision', 'vendor_costs'],
]

type Draft = {
  status: NodeStatus
  revision: number
  updated: string
  blocks?: DraftBlock[]
  sources?: Array<{ ref: string; detail: string }>
  confirmed?: string[]
}

const DRAFTS: Record<string, Draft> = {
  transition_case: {
    status: 'done', revision: 2, updated: '2026-10-21T15:12:00Z',
    blocks: [
      { type: 'paragraph', text: 'The ability for a dismounted team to put its own short-range ISR overhead in GPS-degraded, bandwidth-scarce conditions, without requesting collection from a higher echelon. The capability is embodied in TERN-4, a Group 1 small unmanned aircraft system built by Ternline, Inc.' },
      {
        type: 'table', columns: ['Attribute', 'Value', 'Source'], rows: [
          ['Weight and form', '2.4 kg, backpackable, hand-launched', 'Vendor reports'],
          ['Endurance', '45 minutes at 21C; 39 at 49C; 34 at -10C', 'Vendor reports; temperature figures from an independent lab, November 2024'],
          ['Sensor', 'Stabilized electro-optical and infrared sensor ball', 'Vendor reports'],
          ['Navigation', 'Visual-inertial odometry when GPS is lost; route completed in 31 of 34 attempts under GPS jamming', 'Vendor reports of a government-witnessed exercise, June 2025'],
          ['Datalink', 'H.265 video at 512 kbps sustained, degrading to 256 kbps; behavior beyond 6 km not characterized', 'Vendor reports'],
          ['Integration', 'ATAK 4.10 plugin, maintained by the vendor; Cursor on Target telemetry', 'Vendor reports'],
          ['Environmental', 'MIL-STD-810H subset: high temperature, low temperature to -10C, vibration, rain', 'Independent lab report FRETL-24-1187'],
        ],
      },
      { type: 'heading', text: 'What has been fielded' },
      {
        type: 'bullets', items: [
          '52 aircraft with one special operations unit since March 2025; about 30 operators trained.',
          '8 aircraft with a state public safety agency for search and rescue since June 2025.',
          'Delivered on a $2.3M delivery order (October 2024). The last 8 of 60 slipped five weeks on a motor lead time, which the vendor reported to the customer six weeks ahead.',
        ],
      },
      { type: 'heading', text: 'What transfers' },
      { type: 'paragraph', text: 'The aircraft, controller and ground control application, the ATAK plugin, operator and maintainer training, and depot repair. The navigation stack and the calibration procedure are held largely by two people (section 6), so the technical data package in section 8 decides how much of the capability a receiving office actually gets.' },
    ],
    sources: [
      { ref: QUESTIONNAIRE, detail: 'Capability, fielded quantities and users, exercise results, integration and datalink (Questions 2, 3, 5, 8, 12, 13, 22)' },
      { ref: CAPABILITY, detail: 'Specifications and past performance' },
      { ref: LAB, detail: 'Endurance by temperature; the MIL-STD-810H methods tested' },
    ],
  },
  operational_need: {
    status: 'done', revision: 1, updated: '2026-10-21T15:20:00Z',
    blocks: [
      { type: 'paragraph', text: 'Small dismounted teams need short-range ISR they control. Today a team either waits hours for collection it does not control, or carries a system that depends on a network it does not have.' },
      { type: 'heading', text: 'Documented gaps' },
      {
        type: 'bullets', items: [
          'Organic ISR at the team level when GPS is degraded or denied.',
          'Video that holds up on links of 256 to 512 kbps.',
          'Operation fully disconnected, with no reach-back to a network.',
        ],
      },
      { type: 'heading', text: 'Where the requirement stands' },
      { type: 'callout', label: 'Requirement status:', text: 'No validated requirement document was provided. The setup answer says the using unit has a draft operational need statement that has not been submitted. [NEEDS INPUT] Its title and status, once it is submitted.' },
    ],
    sources: [
      { ref: QUESTIONNAIRE, detail: 'The operational problem and the conditions it was built for (Questions 2, 13)' },
      { ref: 'answer', detail: 'Requirement status' },
    ],
  },
  program_offices: {
    status: 'done', revision: 1, updated: '2026-10-21T16:05:00Z',
    blocks: [
      { type: 'paragraph', text: 'Ranked by mission fit from the documents and the setup answers. Each office is named by its role here: in a live transition the platform matches the budget books and names the offices, their lines and a contact.' },
      {
        type: 'table', columns: ['Rank', 'Office', 'Fit', 'Why'], rows: [
          ['1', 'The current user\'s program office, for a follow-on production buy', 'Strong', 'Already flying 52 aircraft; the research award ends August 2027 and the delivery order has no option year'],
          ['2', 'A service-level Group 1 small UAS product office', 'Strong', 'Same aircraft class and mission; needs the full MIL-STD-810H profile and an authority to operate'],
          ['3', 'A small-unit organic ISR program in a second service', 'Moderate', 'Mission fit; integration against its architecture is untested'],
          ['4', 'A public safety or homeland security grant program', 'Moderate', 'Commercial line already flying 8 aircraft; dual-use revenue would ease the concentration in section 6'],
        ],
      },
      { type: 'heading', text: 'Recommended first approach' },
      { type: 'paragraph', text: 'Start with the current user\'s program office, which can confirm the field record and is the only office with a funded relationship today. A service-level office is the path to scale, but the qualification and authorization work in section 9 comes first.' },
    ],
    sources: [
      { ref: QUESTIONNAIRE, detail: 'Current users, funding history and contract terms (Questions 3, 4, 20)' },
      { ref: 'answer', detail: 'Offices already in mind; existing relationships' },
      { ref: 'upstream:transition_case', detail: 'The capability' },
      { ref: 'upstream:operational_need', detail: 'The need' },
    ],
  },
  technical_readiness: {
    status: 'done', revision: 1, updated: '2026-10-21T16:40:00Z',
    blocks: [
      { type: 'paragraph', text: 'The platform computes each level from the cited evidence. All three of the vendor\'s self-scores hold: TRL 7, MRL 5, IRL 5.' },
      {
        type: 'table', columns: ['Ladder', 'Declared', 'Derived', 'Evidence'], rows: [
          ['TRL', '7', '7', '18 months of operational use with the using unit; a government-witnessed exercise at 1,900 m and 38C; an independent MIL-STD-810H subset'],
          ['MRL', '5', '5', '60 production-representative aircraft built by hand in-house; no pilot line and no certified quality system'],
          ['IRL', '5', '5', 'ATAK 4.10 tested on the using unit\'s devices; no service-level architecture, managed device baseline or cloud enclave tested'],
        ],
      },
      { type: 'heading', text: 'Test evidence' },
      {
        type: 'bullets', items: [
          'More than 1,400 logged internal flight-test sorties.',
          'June 2025 exercise flown by the using unit: 140 sorties, 96 flight hours, route completed in 31 of 34 attempts under GPS jamming.',
          'Independent lab, November 2024: high temperature, low temperature to -10C, vibration and rain all passed.',
        ],
      },
      { type: 'heading', text: 'Not yet tested' },
      {
        type: 'bullets', items: [
          'Cold below -10C; the vendor expects endurance to fall materially.',
          'Salt fog and maritime exposure.',
          'Electronic warfare beyond a training-grade emitter.',
          'Datalink behavior beyond 6 km.',
          'Winds above 15 m/s: three airframes lost in 1,400 sorties, all in those winds.',
        ],
      },
    ],
    sources: [
      { ref: QUESTIONNAIRE, detail: 'Testing, limits and the self-assessed levels (Questions 5 to 14)' },
      { ref: LAB, detail: 'Environmental test results and scope' },
    ],
  },
  company_readiness: {
    status: 'needs_input', revision: 1, updated: '2026-10-22T09:30:00Z',
    blocks: [
      { type: 'paragraph', text: 'CRL 1, computed: the lowest of the three pillars, never the average. The vendor self-scored CRL 2.' },
      {
        type: 'table', columns: ['Pillar', 'Level', 'To reach the next level'], rows: [
          ['Financial', '2', 'Balanced revenue: 55 percent sits with one customer'],
          ['Execution', '2', 'Technical density of at least 60 percent (58 percent today)'],
          ['Security and mission alignment', '1', 'A foreign-ownership screening, which needs a sponsor'],
        ],
      },
      { type: 'callout', label: 'Binding pillar:', text: 'Security and mission alignment. No facility clearance has been sponsored, so no foreign-ownership determination has been made. The vendor flags a non-U.S. limited partner holding under 2 percent with no rights.' },
      { type: 'heading', text: 'Financial' },
      { type: 'paragraph', text: '$4.1M cash and $310K average monthly net burn as of 31 August 2026: about 13 months of runway, under the 18-month floor for a procurement timeline. These are the company\'s management accounts; the FY2025 statements were reviewed, not audited. [NEEDS INPUT] Whether the reviewed statements can be shared.' },
      { type: 'heading', text: 'Execution' },
      { type: 'paragraph', text: '38 staff, 22 technical. Key-person risk is high: the navigation software lead and the calibration technician each hold work nobody else can do, and losing either would cost three to four months. Cross-training on the navigation stack began in July 2026.' },
      { type: 'heading', text: 'Security and mission alignment' },
      { type: 'paragraph', text: 'NIST SP 800-171 score of 68 of 110, posted to SPRS 14 March 2026. CMMC Level 2 assessment scheduled for March 2027. An SBOM exists for the flight software only. Nine of 38 staff have military or defense program experience.' },
      { type: 'heading', text: 'Conditions to reach CRL 4' },
      {
        type: 'bullets', items: [
          'A sponsored facility clearance and a foreign-ownership determination. [NEEDS INPUT] The sponsoring office.',
          'Revenue concentration below half, or a follow-on award beyond August 2027.',
          'A second engineer able to modify the navigation stack, and the calibration procedure written down (vendor target: December 2026).',
          'CMMC Level 2 held, and an SBOM for the ground control application.',
        ],
      },
    ],
    sources: [
      { ref: QUESTIONNAIRE, detail: 'Cash, burn, revenue, staffing, investors, compliance and clearances (Questions 15 to 25)' },
      { ref: 'answer', detail: 'Where the financial and compliance figures come from' },
      { ref: 'upstream:technical_readiness', detail: 'Technical levels' },
    ],
  },
  vendor_costs: {
    status: 'review', revision: 2, updated: '2026-10-22T10:15:00Z',
    blocks: [
      { type: 'paragraph', text: 'All figures are quoted by Ternline, Inc. as of 12 September 2026 and valid for 180 days. Beyond that they escalate on the BLS Employment Cost Index for aerospace manufacturing, floored at zero and capped at 4 percent a year; batteries and motors pass through at cost plus 12 percent. No figure has been verified by the government.' },
      { type: 'heading', text: 'One-time costs' },
      {
        type: 'table', columns: ['Element', 'Cost', 'Status', 'Note'], totals: { column: 'Cost', by: 'Status' }, rows: [
          ['Ruggedized airframe variant', '$95,000', 'Required', 'Government configuration of the fielded airframe'],
          ['Qualification and first-article test', '$210,000', 'Required', 'The full MIL-STD-810H profile, not the subset tested so far'],
          ['Authorization engineering support', '$105,000', 'Required', '600 hours at $175; the vendor\'s least confident figure'],
          ['Integration engineering', '$192,500', 'Required', '1,100 hours at $175; the 500 enclave hours are the soft part'],
          ['Technical data package', '$240,000', 'Elective', 'About five months, including writing down the calibration procedure'],
        ],
      },
      { type: 'heading', text: 'Unit price' },
      {
        type: 'table', columns: ['Quantity', 'Unit price', 'Includes'], rows: [
          ['1', '$38,000', 'Aircraft, one controller, two battery sets'],
          ['25', '$31,200', 'Same'],
          ['50', '$27,500', 'Same'],
          ['200', '$23,400', 'Same; holds above 200 until a second calibration fixture is paid for'],
        ],
      },
      { type: 'heading', text: 'Recurring and per-site costs' },
      {
        type: 'table', columns: ['Element', 'Price', 'Basis'], rows: [
          ['Site activation', '$12,000 per site', 'Two vendor staff for three days; excludes a DD Form 1494 action'],
          ['Ground control license', '$1,200 per seat a year', 'Or $95,000 a year enterprise, better above about 80 seats'],
          ['Support and maintenance', '$4,950 per system a year', '18 percent of the quantity-50 price; depot repair in 15 working days'],
          ['Batteries', 'About $340 a set', '300-cycle life; excluded from support'],
          ['Operator course', '$18,000', 'Up to eight students, four days'],
          ['Maintainer course', '$11,000', 'Up to six students, two days'],
          ['Train-the-trainer', '$45,000 one time', 'Courseware, instructor kit, up to four instructors certified'],
          ['Disposal', '$340 per aircraft', 'Battery disposal, storage sanitization, shipping'],
        ],
      },
    ],
    confirmed: ['2:0', '2:1'],
    sources: [
      { ref: QUESTIONNAIRE, detail: 'Every cost figure, rate, quantity, validity and escalation basis (Questions 26 to 35)' },
    ],
  },
  ownership_rights: {
    status: 'done', revision: 1, updated: '2026-10-22T11:00:00Z',
    blocks: [
      { type: 'paragraph', text: 'Development was mixed: the autonomy stack was built under Air Force SBIR Phase I and II awards, and the airframe largely at private expense.' },
      { type: 'heading', text: 'Where the rights sit' },
      {
        type: 'bullets', items: [
          'Software and data produced under the SBIR awards carry SBIR data rights. [NEEDS INPUT] The vendor\'s data rights assertions list.',
          'The airframe and production tooling are the vendor\'s, developed at private expense by its own account.',
        ],
      },
      { type: 'heading', text: 'Technical data that exists' },
      {
        type: 'bullets', items: [
          'Firmware in a controlled repository with tagged releases; an assembly traveler for each airframe.',
          'The calibration procedure is not written down (target December 2026), and neither is the navigation stack tuning.',
        ],
      },
      { type: 'heading', text: 'Recommended rights position' },
      { type: 'callout', label: 'Recommended:', text: 'Buy the technical data package ($240,000) with the first production order, and take the government purpose rights option ($600,000 if exercised within 24 months). The change-of-control trigger costs nothing and should be taken either way; unlimited rights including the navigation stack ($1.4M) are worth pricing again once the stack is documented.' },
    ],
    sources: [
      { ref: QUESTIONNAIRE, detail: 'Funding history, documentation and data rights prices (Questions 4, 17, 28)' },
      { ref: 'answer', detail: 'How development was funded; what the receiving office needs to do' },
      { ref: 'upstream:program_offices', detail: 'Receiving offices' },
    ],
  },
}

// A draft's plain text, as the server renders `content`.
const render = (blocks: DraftBlock[]) => blocks.map((b) =>
  b.type === 'bullets' ? (b.items ?? []).map((i) => `- ${i}`).join('\n')
    : b.type === 'table' ? [b.columns ?? [], ...(b.rows ?? [])].map((r) => r.join(' | ')).join('\n')
      : b.type === 'callout' ? `${b.label ?? ''} ${b.text ?? ''}`.trim()
        : b.text ?? '').join('\n\n')

export const SAMPLE_GRAPH: TransitionGraph = {
  nodes: DEFINITIONS.map((d): GraphNode => {
    const draft = DRAFTS[d.key]
    return {
      id: `sample-node-${d.key}`,
      transition_id: T,
      node_key: d.key,
      status: draft?.status ?? 'blocked',
      content: draft?.blocks ? render(draft.blocks) : '',
      data: draft?.blocks ? { blocks: draft.blocks, confirmed: draft.confirmed } : {},
      sources: draft?.sources ?? [],
      revision: draft?.revision ?? 0,
      error: '',
      prompts: '',
      name: d.name,
      description: d.description,
      position: d.position,
      front_end_display_order: d.order,
      confirmed_rows: draft?.confirmed ?? [],
      budget_match: null,
      created_at: CREATED,
      updated_at: draft?.updated ?? CREATED,
    }
  }),
  edges: EDGES.map(([node_key, depends_on_key]) => ({ node_key, depends_on_key })),
}

let ir = 0
const ask = (node: string, question: string, answer: string | null, choices: string[] = [], multiple = false,
  origin: InfoRequest['origin'] = 'intake'): InfoRequest => ({
  id: `sample-ir-${++ir}`,
  transition_node_id: `sample-node-${node}`,
  question,
  answer,
  status: answer === null ? 'open' : 'answered',
  choices,
  allows_multiple_responses: multiple,
  origin,
  answered_by: null,
  answered_at: answer === null ? null : '2026-10-20T15:00:00Z',
  created_at: origin === 'intake' ? CREATED : '2026-10-22T09:30:00Z',
  updated_at: answer === null ? '2026-10-22T09:30:00Z' : '2026-10-20T15:00:00Z',
})

export const SAMPLE_INFO_REQUESTS: InfoRequest[] = [
  ask('transition_case', 'Who uses the technology today?', 'One unit or office, fielded',
    ['One unit or office, fielded', 'Several units in the same community', 'Pilot or experiment users only', 'Not fielded yet'], true),
  ask('transition_case', 'What kind of technology is it?', 'Hardware and software', ['Hardware', 'Software', 'Hardware and software']),
  ask('operational_need', 'Is there a validated requirement document (ICD, CDD, urgent need statement) for this capability? If so, name it.',
    'A draft or submitted one; the using unit has a draft operational need statement, not yet submitted',
    ['Yes, validated', 'A draft or submitted one', 'No', 'Not sure']),
  ask('program_offices', 'Which receiving program offices do you already have in mind, if any?',
    'The current user\'s program office first; a service-level Group 1 small UAS office after qualification'),
  ask('program_offices', 'Who do you already know inside the gaining service (a requirements sponsor, a program manager or PEO, users)? Name the people or offices.',
    'The using unit\'s operators and their program office point of contact'),
  ask('technical_readiness', 'Has it been integrated with the gaining service\'s systems or networks? Which ones?', 'Partly; ATAK on the using unit\'s devices only',
    ['Yes, tested', 'Partly', 'No']),
  ask('company_readiness', 'Can the assessing office obtain verified cash and burn figures for the company, not just the company\'s own statement?',
    'Only the company\'s figures', ['Yes', 'Only the company\'s figures', 'No']),
  ask('company_readiness', 'Where does the company\'s compliance status (NIST SP 800-171 score, CMMC) come from?', 'The posted government record',
    ['The posted government record', 'The company\'s statement', 'Unknown']),
  ask('company_readiness', 'Can Ternline share its reviewed FY2025 financial statements, rather than its management accounts?', null, [], false, 'draft'),
  ask('company_readiness', 'Which office would sponsor Ternline\'s facility clearance and foreign-ownership screening?', null, [], false, 'draft'),
  ask('ownership_rights', 'How was the technology\'s development funded?', 'Mixed', ['Private expense', 'Government funded', 'Mixed', 'Not sure']),
  ask('ownership_rights', 'What does the receiving office need to be able to do with it?', 'Repair and sustain it; Compete future buys',
    ['Build it, or have others build it', 'Repair and sustain it', 'Modify and integrate it', 'Compete future buys'], true),
]

const doc = (id: string, title: string, type: string, bytes: number): TransitDocument => ({
  id, transition_id: T, kind: 'uploaded', document_type: type, title, gcs_object: null,
  mime_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', size_bytes: bytes,
  status: 'ready', error: '', uploaded_by: null, created_at: CREATED, updated_at: CREATED,
})

export const SAMPLE_DOCUMENTS: TransitDocument[] = [
  doc('sample-doc-questionnaire', QUESTIONNAIRE, 'vendor_questionnaire', 12299),
  doc('sample-doc-capability', CAPABILITY, 'vendor_proposal', 2007),
  doc('sample-doc-lab', LAB, 'independent_report', 1623),
]

export const SAMPLE_DOCUMENT_TEXT: Record<string, string> = {
  'sample-doc-questionnaire': texts.questionnaire,
  'sample-doc-capability': texts.capability,
  'sample-doc-lab': texts.lab,
}

export const SAMPLE_READINESS: Readiness = {
  assessed: true,
  out_of_date: false,
  result: readinessResult as ReadinessResult,
  dropped: [],
  events: [],
  job: null,
}

